import { Suspense, lazy, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { PartyUiContext, Stage, readAloud, type PartyUi } from '@gmp/shared-ui';
import { party } from './net/client';
import { navigate, useRoute } from './app/router';
import { sound, usePrefs } from './app/prefs';
import { Home } from './screens/Home';
import { CreateParty } from './screens/Create';
import { Parties } from './screens/Parties';
import { DeviceScreen } from './screens/Device';
import { JoinParty } from './screens/Join';
import { HowToPlay } from './screens/HowTo';
import { Settings } from './screens/Settings';
import { Privacy } from './screens/Privacy';
import { PartyScreen } from './screens/Party';
import { DisplayScreen } from './screens/Display';
import { Waiting } from './screens/Waiting';
import { LoadingScreen } from './app/Loading';
import { Welcome } from './app/Welcome';
import { ErrorBoundary } from './screens/ErrorBoundary';
import { syncTurnAlerts } from './app/TurnAlerts';
import { roomSounds, type RoomSnapshot } from './app/roomSounds';
import { InviteCards } from './app/Invites';
import { AuthDone } from './screens/Account';
import { FriendsScreen } from './screens/Friends';

// Owner-only content admin: its own chunk, fetched only when /admin is opened.
const AdminScreen = lazy(() => import('./screens/Admin').then((m) => ({ default: m.AdminScreen })));

export function usePartyState() {
  return useSyncExternalStore(party.subscribe, party.getState);
}

export function App() {
  const state = usePartyState();
  const route = useRoute();
  const prefs = usePrefs();

  useEffect(() => {
    void party.connect();
    const unlock = () => {
      sound.unlock();
      readAloud.unlock();
    };
    window.addEventListener('pointerdown', unlock, { passive: true });
    // A TV remote's OK button is a key press, not a tap.
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  usePlatformSounds(state);

  // Turn alerts: make sure the server knows this device's subscription after every (re)connect.
  useEffect(() => {
    if (state.status === 'online') void syncTurnAlerts().catch(() => undefined);
  }, [state.status]);

  // In a room: the URL follows (so a refresh lands back in the party).
  useEffect(() => {
    // On the inbox (or the device screen) a re-attached party is stepped out of, not jumped into.
    if (state.room && state.room.you.role !== 'display' && (route.name === 'parties' || route.name === 'device')) party.stepOut();
    // The Friends screen can be opened from a party's lobby (to invite friends) without leaving it.
    else if (state.room && state.room.you.role !== 'display' && route.name !== 'party' && route.name !== 'admin' && route.name !== 'friends' && route.name !== 'auth') navigate({ name: 'party' }, true);
    if (!state.room && route.name === 'party' && state.status === 'online' && !party.hasSavedRoom) navigate({ name: 'home' }, true);
  }, [state.room, route.name, state.status]);

  const ui = useMemo<PartyUi | null>(() => {
    if (!state.room) return null;
    const members = new Map(state.room.members.map((m) => [m.id, m]));
    return {
      now: party.now,
      me: state.room.you.id,
      room: state.room,
      member: (id) => (id ? members.get(id) : undefined),
      act: (a) => party.act(a),
      input: (i) => party.input(i),
      upload: (b, k, p) => party.upload(b, k, p),
      report: (id, reason) => {
        party.report(id, reason);
        party.notify('Reported to the host. Thanks.', 'good');
      },
      display: state.room.you.role === 'display',
      sound,
      reducedMotion: prefs.reducedMotion || state.room.settings.reducedMotion,
    };
  }, [state.room, prefs.reducedMotion]);

  let screen: React.ReactNode;
  if (route.name === 'admin') screen = <Suspense fallback={<Stage><LoadingScreen label="Loading admin…" /></Stage>}><AdminScreen /></Suspense>;
  else if (route.name === 'display') screen = <DisplayScreen code={route.code} />;
  else if (route.name === 'privacy') screen = <Privacy />;
  else if (route.name === 'device') screen = <DeviceScreen />;
  else if (route.name === 'auth') screen = <AuthDone />;
  else if (route.name === 'friends') screen = <FriendsScreen />;
  else if (route.name === 'parties') screen = <Parties />;
  else if (state.room && ui) screen = <PartyScreen />;
  else if (state.pendingCode) screen = <Waiting code={state.pendingCode} />;
  else if (route.name === 'create') screen = <CreateParty initialGame={route.game} />;
  else if (route.name === 'join') screen = <JoinParty initialCode={route.code} />;
  else if (route.name === 'how') screen = <HowToPlay />;
  else if (route.name === 'settings') screen = <Settings />;
  else if (route.name === 'party' && party.hasSavedRoom) screen = <Reconnecting />;
  else screen = <Home />;

  return (
    <div className={prefs.reducedMotion ? 'reduce-motion' : ''}>
      <ErrorBoundary>
        {ui ? <PartyUiContext.Provider value={ui}>{screen}</PartyUiContext.Provider> : screen}
      </ErrorBoundary>
      <Toasts />
      <InviteCards />
      <ConnectionBanner />
      <Welcome />
    </div>
  );
}

/**
 * Platform sound bites: who hears what (TV vs phone), and the cues for room changes (joins, ready, start,
 * reveals, results, rematch). The room's own sound setting switches them off for the whole party.
 */
function usePlatformSounds(state: ReturnType<typeof usePartyState>) {
  const prev = useRef<RoomSnapshot>({ room: null, game: null });
  useEffect(() => {
    const room = state.room;
    sound.tv = room?.you.role === 'display';
    // A TV in the same room takes the room-level cues; a remote party's TV is somewhere else.
    sound.sharedTv = !!room && !sound.tv && !!room.tvConnected && room.settings.playMode !== 'remote';
    sound.roomSound = room?.settings.sound !== false;
    const next = { room, game: state.game };
    for (const s of roomSounds(prev.current, next, room?.you.id ?? null)) {
      if (s.cue === 'countdown') sound.countdown({ scope: s.scope });
      else sound.play(s.cue, { scope: s.scope });
    }
    prev.current = next;
  }, [state.room, state.game]);
}

function Reconnecting() {
  return (
    <Stage>
      <LoadingScreen label="Getting you back in…">
        <button className="btn btn-ghost btn-sm mt-2" onClick={() => { party.leave(); navigate({ name: 'home' }, true); }}>Leave party</button>
      </LoadingScreen>
    </Stage>
  );
}

function Toasts() {
  const { notices, error } = usePartyState();
  const items = [...notices];
  return (
    <div className="fixed bottom-28 inset-x-0 z-50 flex flex-col items-center gap-2 pointer-events-none safe-pad" aria-live="polite">
      <AnimatePresence>
        {items.map((n) => (
          <motion.div key={n.id} initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }} className={`chip text-sm py-2 px-4 shadow-xl ${n.tone === 'good' ? 'bg-good text-ink' : ''}`}>
            {n.text}
          </motion.div>
        ))}
        {error && error.code !== 'network' && (
          <motion.button key="err" initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }} onClick={() => party.clearError()} className="pointer-events-auto chip bg-bad text-white text-sm py-2 px-4 shadow-xl" role="alert">
            {error.message}
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

function ConnectionBanner() {
  const { status, room } = usePartyState();
  if (status === 'superseded')
    return (
      <div className="fixed inset-0 z-[60] bg-ink/95 grid place-items-center p-6 text-center">
        <div className="card p-6 max-w-sm flex flex-col gap-4">
          <p className="font-display font-black text-2xl">Opened somewhere else</p>
          <p className="text-mute">This party is open in another tab or device with your identity.</p>
          <button className="btn" onClick={() => party.reclaim()}>Play here instead</button>
        </div>
      </div>
    );
  if (status === 'online' || (!room && status === 'connecting')) return null;
  return (
    <div className="fixed bottom-3 inset-x-0 z-50 flex justify-center pointer-events-none" role="status">
      <span className="chip bg-[var(--accent-2)] text-ink border-none shadow-xl text-sm py-2 px-4">
        <span className="size-2 rounded-full bg-ink animate-pulse" /> {status === 'connecting' ? 'Reconnecting…' : 'Connection lost. Retrying…'}
      </span>
    </div>
  );
}
